/**
 * Schnellfragen of the Chat-Eingabe block (`dhsv100/chat-input`): up to six
 * short questions rendered as pills under the field. A click sends the pill's
 * text as the visitor's first message, exactly like submitting the field
 * (05-einstiege.md section 3, `#vc-chips`).
 *
 * Markup (server-rendered, `index.blade.php`):
 *
 *   <ul class="dhsv-chat-input__quick" aria-label="Schnellfragen">
 *     <li><button type="button" class="dhsv-chat-input__quick-btn"
 *                 data-dhsv-chat-quick="Was kostet das?">Was kostet das?</button></li>
 *   </ul>
 *
 * The same rules as the PHP side (`index.php`): strings only, whitespace
 * collapsed, trimmed, at most 80 characters each, empty ones dropped, at most six.
 */
import { cleanText } from './proactive-sections';

export const QUICK_QUESTIONS_MAX = 6;
export const QUICK_QUESTION_MAX_LENGTH = 80;
export const QUICK_ATTRIBUTE = 'data-dhsv-chat-quick';
export const QUICK_SELECTOR = `[${ QUICK_ATTRIBUTE }]`;
/** The block wrapper that holds the form and the pills. */
export const QUICK_BLOCK_SELECTOR = '.wp-block-dhsv100-chat-input';

/**
 * One Schnellfrage, cleaned.
 *
 * @param {unknown} value Raw value.
 */
export function cleanQuickQuestion( value: unknown ): string {
	return cleanText( value, QUICK_QUESTION_MAX_LENGTH );
}

/**
 * The block attribute, cleaned: at most six non-empty strings of at most 80
 * characters. Anything but an array yields none.
 *
 * @param {unknown} value Raw attribute value.
 */
export function sanitizeQuickQuestions( value: unknown ): string[] {
	if ( ! Array.isArray( value ) ) {
		return [];
	}
	const out: string[] = [];
	for ( const item of value ) {
		const text = cleanQuickQuestion( item );
		if ( text !== '' ) {
			out.push( text );
		}
		if ( out.length === QUICK_QUESTIONS_MAX ) {
			break;
		}
	}
	return out;
}

export type QuickQuestionTarget = {
	button: HTMLElement;
	/** The question to send. */
	text: string;
	/** Position among the block's pills (0-based), for tracking. */
	index: number;
	/** The block's chat form, when there is one. */
	form: HTMLFormElement | null;
};

/**
 * The Schnellfrage a click landed on, or `null` (not a pill, disabled, empty).
 *
 * @param {Element|null} start Where the event came from.
 */
export function findQuickQuestion(
	start: Element | null
): QuickQuestionTarget | null {
	const button = start?.closest< HTMLElement >( QUICK_SELECTOR ) ?? null;
	if (
		! button ||
		button.hasAttribute( 'disabled' ) ||
		button.getAttribute( 'aria-disabled' ) === 'true'
	) {
		return null;
	}
	const text = cleanQuickQuestion( button.getAttribute( QUICK_ATTRIBUTE ) );
	if ( text === '' ) {
		return null;
	}
	const block = button.closest( QUICK_BLOCK_SELECTOR );
	const siblings = block
		? Array.from( block.querySelectorAll( QUICK_SELECTOR ) )
		: [ button ];
	return {
		button,
		text,
		index: Math.max( 0, siblings.indexOf( button ) ),
		form:
			block?.querySelector< HTMLFormElement >(
				'form[data-dhsv-chat-form]'
			) ?? null,
	};
}
